/**
 * Lighting per scene (art §6): key direction, band thresholds T1/T2, shadow tint, rim, hatching, keyline — plus the
 * fixed material ramps (§4.2-D) derived on the CPU with the scene's shadow tint (§2.3) for the `mats` uniform.
 */
import { MATERIAL_MIDS, TOKENS, hexToLinear, sceneRamp, type SceneTintId } from '../content/palette';
import { MATR, MAT_RAMP_COUNT } from './gpu/layouts';

export type LightingId = 'arena' | 'diaper' | 'goldGround' | 'constructor' | 'crypt' | 'cardTable' | 'folio';

export interface LightingPreset {
  key: [number, number, number];
  T1: number; T2: number;
  tint: SceneTintId;
  rim: { color: string; strength: number; threshold: number } | null;
  hatchPx: number; hatchStrength: number;
  keyline: boolean;
  hullK: number;
  floorA: string; floorB: string;
}

export const LIGHTING: Record<LightingId, LightingPreset> = {
  arena: { key: [-0.66, 0.64, 0.36], T1: 0.42, T2: 0.72, tint: 'arena', rim: { color: TOKENS.goldLight, strength: 0.45, threshold: 0.55 }, hatchPx: 4.5, hatchStrength: 1, keyline: true, hullK: 1, floorA: TOKENS.terracotta, floorB: TOKENS.vellumAged },
  diaper: { key: [-0.66, 0.64, 0.36], T1: 0.42, T2: 0.72, tint: 'diaper', rim: { color: TOKENS.leadWhite, strength: 0.35, threshold: 0.55 }, hatchPx: 4.5, hatchStrength: 1, keyline: true, hullK: 1, floorA: TOKENS.terracotta, floorB: TOKENS.vellumAged },
  goldGround: { key: [-0.4, 0.8, 0.45], T1: 0.38, T2: 0.7, tint: 'goldGround', rim: null, hatchPx: 4.5, hatchStrength: 1, keyline: false, hullK: 1.25, floorA: TOKENS.ochre, floorB: TOKENS.vellumAged },
  constructor: { key: [-0.65, 0.55, 0.52], T1: 0.4, T2: 0.74, tint: 'constructor', rim: { color: TOKENS.leadWhite, strength: 0.25, threshold: 0.6 }, hatchPx: 4.5, hatchStrength: 0.8, keyline: false, hullK: 1, floorA: TOKENS.ochre, floorB: TOKENS.sinopia },
  crypt: { key: [0.2, 0.5, 0.8], T1: 0.48, T2: 0.8, tint: 'dungeon', rim: { color: TOKENS.minium, strength: 0.5, threshold: 0.5 }, hatchPx: 4, hatchStrength: 1.1, keyline: true, hullK: 1, floorA: TOKENS.umber, floorB: '#4A3A2C' },
  cardTable: { key: [0.0, 0.95, 0.3], T1: 0.3, T2: 0.62, tint: 'map', rim: null, hatchPx: 4.5, hatchStrength: 0.6, keyline: false, hullK: 1, floorA: TOKENS.vellum, floorB: TOKENS.vellumAged },
  folio: { key: [-0.3, 0.9, 0.3], T1: 0.3, T2: 0.65, tint: 'map', rim: null, hatchPx: 4.5, hatchStrength: 0.5, keyline: false, hullK: 1, floorA: TOKENS.vellum, floorB: TOKENS.vellumAged },
};

/** Writes the fixed material ramps for a scene into the `mats` host mirror (MAT_RAMP_COUNT × 3 vec4). */
export function writeMaterialRamps(out: Float32Array, preset: LightingPreset): void {
  const put = (idx: number, hex: string, flesh = false): void => {
    const r = sceneRamp(hex, preset.tint, flesh);
    const at = idx * 12;
    out.set([...r.light, 1, ...r.mid, 1, ...r.shadow, 1], at);
  };
  put(MATR.leather, MATERIAL_MIDS.leather);
  put(MATR.wood, MATERIAL_MIDS.wood);
  put(MATR.iron, MATERIAL_MIDS.iron);
  put(MATR.stone, MATERIAL_MIDS.stone);
  put(MATR.horn, TOKENS.leadWhite);
  put(MATR.shell, MATERIAL_MIDS.shell);
  put(MATR.parchment, TOKENS.vellum);
  put(MATR.floorA, preset.floorA);
  put(MATR.floorB, preset.floorB);
  put(MATR.plumage, TOKENS.leadWhite);
  put(MATR.sclera, TOKENS.leadWhite);
  put(MATR.gem, TOKENS.lapisLight);
  if (out.length < MAT_RAMP_COUNT * 12) throw new Error('mats mirror too small');
}

/** Normalised key direction. */
export function keyDir(p: LightingPreset): [number, number, number] {
  const l = Math.hypot(...p.key);
  return [p.key[0] / l, p.key[1] / l, p.key[2] / l];
}

export const rimLinear = (p: LightingPreset): [number, number, number] => (p.rim ? hexToLinear(p.rim.color) : [0, 0, 0]);
